import { SCHEMA } from '@vedalib/editor/lib/brand'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { Typography } from 'components/uiKit/Typography'
import { BrandButtonFont, BrandFieldShadow } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import type { CourseProgressFormBrandType } from './courseProgressTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.courseProgress.style, {
  chartEmpty: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressBackground'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  chartFill: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressCompleted'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  progress__var: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
    gap: genBrandField({
      as: FieldNumber,
      label: t('elements.courseProgress.schema.indents'),
      props: { width: 64 },
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.courseProgress.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const CourseProgressFormStyle: CourseProgressFormBrandType = ({
  device,
  subConfigs,
  element,
  edited,
  setForm,
}) => {
  const { font } = element || {}
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const values = useElementFontValues('courseProgress', device, font)
  const styles = useElementFontCss('courseProgress', device, font)

  const handleSetTypography = useCallback(() => setForm?.('base'), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.courseProgress.tags.progress')}</Typography>
        <FormField config={schema.chartEmpty.color} />
        <FormField config={schema.chartFill.color} />
        <FormField config={schema.progress__var.borderRadius} />
        <FormField config={schema.progress__var.borderStyle} />
        <FormField config={schema.progress__var.borderColor} />
        <FormField config={schema.progress__var.borderWidth} />
        <FormField config={schema.progress__var.gap} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <BrandButtonFont
          edited={edited?.font?.base}
          font='base'
          onClick={handleSetTypography}
          styles={styles.base}
          values={values.base}
        >
          {t('page.brand.tab.typography')}
        </BrandButtonFont>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(CourseProgressFormStyle) as typeof CourseProgressFormStyle
